import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
import { calculateOptimalLineHeight } from '../../utils/styles';
import BoxDraftText from '../common/BoxDraftText';
import { collectLinks, computeLinkGeometry, findLink } from './shapeLinks';
import { hasTextContent, linkLabelPoint, textLayoutOf, verticalJustify } from './shapeText';

// Les textes d'une forme composée sont du DOM (Draft) : Konva ne sait pas en
// porter. Ils sont donc posés par-dessus le Stage, dans le même repère que lui
// (`margin` compris, cf. getShapeComponent) — un texte de sous-forme sur son
// cadre, tourné avec elle ; un libellé de lien au milieu de son tracé, toujours
// horizontal. Hors édition, ils laissent passer le pointeur vers le canvas.

// Cadre d'un texte de sous-forme, aussi appliqué directement pendant un geste
// (cf. ShapeFullscreenEditor#refreshLinksLive).
export const applySubShapeTextFrame = (element, sub, margin) => {
  Object.assign(element.style, {
    left: `${margin + (sub.x ?? 0)}px`,
    top: `${margin + (sub.y ?? 0)}px`,
    width: `${sub.width}px`,
    height: `${sub.height}px`,
    transform: `rotate(${sub.rotation ?? 0}deg)`,
  });
};

export const applyLinkLabelPosition = (element, point, margin) => {
  if (!point) return;
  Object.assign(element.style, { left: `${margin + point[0]}px`, top: `${margin + point[1]}px` });
};

const TEXT_PADDING = 4;
const LINK_LABEL_MAX_WIDTH = 200;

const ShapeText = ({ box, isEditing, readState, writeState, textAlign, style }) => {
  const { t } = useTranslation();
  return (
    <BoxDraftText
      box={box}
      contentKey='shapeText'
      isSelected={isEditing}
      readState={readState}
      writeState={writeState}
      textAlign={textAlign}
      placeholder={isEditing ? t('shape-text-placeholder') : ''}
      style={style}
    />
  );
};

const SubShapeText = ({ box, sub, margin, isEditing, baseStyle, registerNode }) => {
  const { align, verticalAlign } = textLayoutOf(sub);
  const readState = useMemo(() => (source) => source.content?.shapes?.find((s) => s.id === sub.id)?.textEditorState, [sub.id]);
  const writeState = useMemo(() => (draftBox, editorState) => {
    const target = draftBox.content.shapes.find((s) => s.id === sub.id);
    if (target) target.textEditorState = editorState;
  }, [sub.id]);
  return (
    <div
      ref={(element) => {
        if (element) applySubShapeTextFrame(element, sub, margin);
        registerNode?.(`sub:${sub.id}`, element);
      }}
      style={{
        position: 'absolute',
        transformOrigin: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: verticalJustify(verticalAlign),
        padding: TEXT_PADDING,
        boxSizing: 'border-box',
        pointerEvents: isEditing ? 'auto' : 'none',
        ...(isEditing ? { outline: '1px dashed #1677ff', cursor: 'text' } : {}),
      }}
    >
      <ShapeText
        box={box}
        isEditing={isEditing}
        readState={readState}
        writeState={writeState}
        textAlign={align}
        style={{ ...baseStyle, width: '100%' }}
      />
    </div>
  );
};

const LinkLabel = ({ box, entry, margin, isEditing, baseStyle, registerNode }) => {
  const { ownerId, link, from, to } = entry;
  const readState = useMemo(() => (source) => findLink(source.content?.shapes, ownerId, link.id)?.textEditorState, [ownerId, link.id]);
  const writeState = useMemo(() => (draftBox, editorState) => {
    const target = findLink(draftBox.content.shapes, ownerId, link.id);
    if (target) target.textEditorState = editorState;
  }, [ownerId, link.id]);
  const point = linkLabelPoint(computeLinkGeometry(link.type, from, to, link));
  if (!point) return null;
  return (
    <div
      ref={(element) => {
        if (element) applyLinkLabelPosition(element, point, margin);
        registerNode?.(`link:${link.id}`, element);
      }}
      style={{
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        // Le conteneur des textes est de largeur nulle : sans largeur propre,
        // le libellé se réduirait à sa plus petite largeur (une lettre par ligne).
        width: 'max-content',
        maxWidth: LINK_LABEL_MAX_WIDTH,
        minWidth: isEditing ? 40 : undefined,
        padding: '1px 4px',
        // Fond blanc : le trait passe sous le libellé au lieu de le barrer.
        background: 'white',
        pointerEvents: isEditing ? 'auto' : 'none',
        ...(isEditing ? { outline: '1px dashed #1677ff', cursor: 'text' } : {}),
      }}
    >
      <ShapeText
        box={box}
        isEditing={isEditing}
        readState={readState}
        writeState={writeState}
        textAlign='center'
        style={baseStyle}
      />
    </div>
  );
};

// `editing` : { type: 'sub', subId } ou { type: 'link', ownerId, linkId }, le
// texte en cours d'édition (éditeur plein écran seulement). `registerNode`
// (clé, élément) : pour les mises à jour directes pendant un geste.
const ShapeTextOverlay = ({ box, margin = 0, editing = null, registerNode }) => {
  const configuration = useStore((state) => state.configuration);
  const shapes = box.content?.shapes ?? [];
  const containerRef = useRef(null);
  // Texte typographié comme une box texte du document « Défaut » : la modale
  // vit hors des pages, rien n'y hérite de leur police.
  const baseStyle = {
    fontFamily: configuration.fontFamily,
    fontSize: configuration.fontSize,
    color: configuration.baseFontColor?.style,
    lineHeight: calculateOptimalLineHeight(configuration.fontSize),
    wordBreak: 'break-word',
  };

  // Entrer en édition met le curseur dans le texte, sans second clic.
  const editingKey = editing && (editing.type === 'sub' ? `sub:${editing.subId}` : `link:${editing.linkId}`);
  useEffect(() => {
    if (!editingKey) return undefined;
    const frame = requestAnimationFrame(() => {
      containerRef.current?.querySelector(`[data-text-key="${editingKey}"] [contenteditable="true"]`)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [editingKey]);

  return (
    <div ref={containerRef} style={{ position: 'absolute', left: 0, top: 0, width: 0, height: 0 }}>
      {shapes.map((sub) => {
        const isEditing = editing?.type === 'sub' && editing.subId === sub.id;
        if (!isEditing && !hasTextContent(sub.textEditorState)) return null;
        return (
          <div key={sub.id} data-text-key={`sub:${sub.id}`}>
            <SubShapeText box={box} sub={sub} margin={margin} isEditing={isEditing} baseStyle={baseStyle} registerNode={registerNode} />
          </div>
        );
      })}
      {collectLinks(shapes).map((entry) => {
        const isEditing = editing?.type === 'link' && editing.linkId === entry.link.id;
        if (!isEditing && !hasTextContent(entry.link.textEditorState)) return null;
        return (
          <div key={entry.link.id} data-text-key={`link:${entry.link.id}`}>
            <LinkLabel box={box} entry={entry} margin={margin} isEditing={isEditing} baseStyle={baseStyle} registerNode={registerNode} />
          </div>
        );
      })}
    </div>
  );
};

export default ShapeTextOverlay;
